import React from 'react';
import Layout from './Layout';
import {
  accountsMail,
  accountsPhone,
  accountsTooltip,
  profileIc,
} from 'assets/images/images';
import CardSummary from 'components/CardSummary';
import FlaggedVsUnflaggedChart from './charts/FlaggedVsUnflaggedChart';
import AccountEventsChart from './charts/AccountEventsChart';
import ReactSpeedometer from 'react-d3-speedometer';
import {useLocation, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import {
  fetchAccountDetails,
  fetchFlaggedAndUnflaggedChart,
} from '../../../../../requests/queries/flows';
import moment from 'moment';
import {SkeletonLoader} from 'components';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';

const getInitials = name => {
  if (!name || typeof name !== 'string') return '??';
  const parts = name.split(' ');
  if (parts.length === 0 || parts[0] === '') return '??';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

function SingleAccount() {
  const {state} = useLocation();
  const {id} = useParams();
  const [period, setPeriod] = React.useState('day');

  const {
    data: accountDetailsResponse,
    isLoading,
    isError,
  } = useQuery(
    [
      'users-accounts-details',
      {
        account_id: state?.id || id,
      },
    ],
    () =>
      fetchAccountDetails({
        account_id: state?.id || id,
      }),
    {
      retry: 1,
      enabled: !!(state?.id || id),
    },
  );

  const {data: flaggedUnflaggedData, isLoading: isChartLoading} = useQuery(
    [
      'flagged-unflagged-chart',
      {
        account_id: state?.id || id,
        period: period,
      },
    ],
    () =>
      fetchFlaggedAndUnflaggedChart({
        account_id: state?.id || id,
        params: {period},
      }),
    {
      retry: 1,
      enabled: !!(state?.id || id),
      refetchOnWindowFocus: false,
    },
  );

  const totalCredit =
    accountDetailsResponse?.totalTransactions?.find(t => t.type === 'deposit')
      ?.total_amount || '0';

  const totalDebit =
    accountDetailsResponse?.totalTransactions?.find(t => t.type === 'transfer')
      ?.total_amount || '0';

  const totalCases = accountDetailsResponse?.totalCases || '0';

  const averageRiskScore = Math.ceil(
    accountDetailsResponse?.averageRiskScore || 0,
  );
  const riskScoreValue = averageRiskScore > 100 ? 100 : averageRiskScore;

  const userEntity = accountDetailsResponse?.entity;

  const renderUserDetailsSkeleton = () => (
    <div className="flex flex-col gap-2 flex-wrap w-full">
      <SkeletonLoader
        style={{height: '28px', width: '60%', marginBottom: '10px'}}
      />
      <div className="flex items-center gap-4 flex-wrap">
        <SkeletonLoader
          style={{height: '48px', width: '220px', borderRadius: '8px'}}
        />
        <SkeletonLoader
          style={{height: '48px', width: '180px', borderRadius: '8px'}}
        />
      </div>
      <div className="flex items-center flex-wrap gap-2   mt-2">
        <SkeletonLoader style={{height: '16px', width: '150px'}} />
        <div className="h-[1px] w-[10px] bg-gray-300" />
        <SkeletonLoader style={{height: '16px', width: '150px'}} />
      </div>
    </div>
  );

  const renderUserDetails = entity => (
    <div className="flex flex-col gap-2 flex-wrap">
      <h3 className="text-black font-semibold text-[20px]">
        {entity.user_name || 'N/A'}
      </h3>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="px-2 py-1 bg-[#eef3fc] flex items-center flex-wrap gap-[10px] rounded-lg border border-[#BFD3F3]">
          <img src={accountsMail} alt="Email" />
          <p className="text-sm text-[#0B1C56]">{entity.user_email || 'N/A'}</p>
        </div>
        <div className="px-2 py-1 bg-[#eef3fc] flex items-center flex-wrap gap-[10px] rounded-lg border border-[#BFD3F3]">
          <img src={accountsPhone} alt="Phone" />
          <p className="text-sm text-[#0B1C56]">
            {entity.user_mobile ? `+${entity.user_mobile}` : 'N/A'}
          </p>
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-2  ">
        <p className="text-[#868C98] text-sm">
          Registration Date:{' '}
          <span className="font-medium text-black">
            {entity.registration_time
              ? moment(entity.registration_time).format('MMM DD, YYYY')
              : 'N/A'}
          </span>
        </p>
      </div>
    </div>
  );

  const handlePeriodChange = newPeriod => {
    setPeriod(newPeriod);
  };

  if (isError) {
    return (
      <Layout pageTitle="Customer Overview" pageIcon={profileIc}>
        <div className="text-red-500 mt-8 text-center">
          Error fetching account details. Please try again later.
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Customer Overview" pageIcon={profileIc}>
      <div className="grid lg:grid-cols-3 w-full relative mt-3 flex-wrap gap-4">
        <div className="flex items-center gap-4 lg:col-span-2">
          {isLoading ? (
            <SkeletonLoader className="rounded-full min-w-[115px] !w-[115px] !h-[115px] min-h-[115px]" />
          ) : (
            <>
              <div className="min-w-[115px] min-h-[115px] rounded-full flex items-center justify-center bg-[#EEF3FC]">
                <h1 className="text-brandBlue text-[64px]">
                  {userEntity ? getInitials(userEntity.user_name) : '??'}
                </h1>
              </div>
            </>
          )}
          {isLoading ? (
            renderUserDetailsSkeleton()
          ) : userEntity ? (
            renderUserDetails(userEntity)
          ) : (
            <div className="flex flex-col gap-2 flex-wrap">
              <p>Account details not found.</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-1 flex items-center justify-center">
          <div className="relative">
            <p className="absolute bottom-6 left-[-0.5rem] uppercase   font-medium text-[#55A249] text-xs">
              LOW
            </p>
            <p className="absolute bottom-6 right-[-0.5rem] uppercase text-xs   font-medium text-[#B42318]">
              High
            </p>
            <p className="absolute top-0 right-[-0.5rem] text-black font-semibold text-[24px]">
              {averageRiskScore}
            </p>
            <ReactSpeedometer
              value={riskScoreValue}
              maxValue={100}
              needleHeightRatio={0.7}
              labelFontSize={'12px'}
              valueTextFontSize={'23px'}
              textColor="transparent"
              height={150}
              width={250}
              ringWidth={35}
              paddingHorizontal={10}
              forceRender={true}
              needleColor="#033971"
              segments={5}
              segmentColors={[
                '#2E7D32',
                '#9CCC65',
                '#FFCA28',
                '#FFA726',
                '#B42318',
              ]}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 grid lg:grid-cols-3 gap-4">
        <CardSummary
          title="Total Credit"
          body={shortenLargeNumbers(totalCredit, 'NGN', {startAt: 'B'})}
          className="!bg-white"
          volumeValue={
            accountDetailsResponse?.totalTransactions?.find(
              t => t.type === 'deposit',
            )?.count || '0'
          }
          loading={isLoading}
        />
        <CardSummary
          title="Total Debit"
          body={shortenLargeNumbers(totalDebit, 'NGN', {startAt: 'B'})}
          className="!bg-white"
          volumeValue={
            accountDetailsResponse?.totalTransactions?.find(
              t => t.type === 'transfer',
            )?.count || '0'
          }
          loading={isLoading}
        />
        <CardSummary
          title="Total Cases"
          body={totalCases.toString()}
          className="!bg-white"
          loading={isLoading}
        />
      </div>

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <div className="h-[420px] lg:col-span-2">
          <FlaggedVsUnflaggedChart
            data={flaggedUnflaggedData}
            loading={isChartLoading}
            period={period}
            onPeriodChange={handlePeriodChange}
          />
        </div>
        <div className="h-[420px lg:col-span-1">
          <AccountEventsChart
            loading={isLoading}
            accountsTooltip={accountsTooltip}
            totalStatuses={accountDetailsResponse?.totalStatuses}
          />
        </div>
      </div>
    </Layout>
  );
}

export default SingleAccount;
